import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { BehaviorSubject, Observable, firstValueFrom, map, shareReplay } from 'rxjs';
import Swal from 'sweetalert2';
import { Envelope, EnvelopeItem, EnvelopeItemSignaturePayload, EnvelopeStatus } from './envelope.model';
import { FirestoreService } from 'src/app/v2/services/firestore.service';
import { Collection } from 'src/app/v2/models/collection-reference.model';
import { environment } from 'src/environments/environment';

/**
 * Manages envelope documents — bundles of waivers tied to a booking and
 * recipient. Persists to the top-level Firestore `envelopes` collection and
 * keeps a local cache so sync getters (`getItem`, `getEnvelopes`) and the
 * `envelopes$` stream stay snappy. Async getters/setters hit Firestore on
 * cache miss.
 */
@Injectable({
  providedIn: 'root'
})
export class EnvelopeService {

  private envelopes: Envelope[] = [];

  /**
   * Memoized per-id streams so concurrent watchers (e.g. the booking detail
   * view + the envelope-sign page open in another tab) share a single
   * underlying Firestore listener via `shareReplay`. `refCount: true` tears
   * the listener down when the last subscriber leaves, so we don't burn
   * reads on streams nobody is watching.
   */
  private envelopeStreams = new Map<string, Observable<Envelope | undefined>>();

  /**
   * Stream of the current envelope cache. Emits a fresh snapshot
   * (deep-cloned via {@link getEnvelopes}) on every mutation so subscribers
   * can't reach back into the private store.
   */
  public envelopes$ = new BehaviorSubject<Envelope[]>([]);

  constructor(
    private firestoreService: FirestoreService,
    private afs: AngularFirestore,
    private afFun: AngularFireFunctions,
    private http: HttpClient,
  ) {}

  /**
   * Calls a Firebase callable function via Firebase Hosting's URL rewrite
   * (`/api/<name>` → function) instead of the public `cloudfunctions.net`
   * URL. The Hosting layer invokes the function as the Hosting service
   * account, which has invoker permission by default — so the function
   * doesn't need an `allUsers` IAM binding, sidestepping the
   * `iam.allowedPolicyMemberDomains` org-policy block that has bitten
   * every new CF in this project (see TODO.md).
   *
   * Uses the callable wire format under the hood: `{ data: payload }` in
   * the POST body, `{ result: ... }` in the response. The function code
   * itself doesn't change — it still sees `data` as `raw` because the
   * Hosting proxy passes the request through to the same callable runtime.
   *
   * Local-dev caveat: this URL only resolves when served behind Firebase
   * Hosting (deployed app, or `firebase emulators:start --only
   * hosting,functions`). A plain `ng serve` workflow will get a 404 on
   * `/api/*` since there's no Hosting layer in front of it.
   */
  private async callViaHostingRewrite<T = unknown>(name: string, payload: unknown): Promise<T> {
    const url = `/api/${name}`;
    const response = await firstValueFrom(
      this.http.post<{ result?: T }>(url, { data: payload }),
    );
    return (response?.result as T);
  }

  /**
   * Cache-only read of every envelope seen so far. Deep-cloned so callers
   * can mutate the result safely. Use {@link getEnvelopeById} for reliable
   * by-id lookups (falls back to Firestore on cache miss).
   */
  getEnvelopes(): Envelope[] {
    return this.envelopes.map(e => this.clone(e));
  }

  /**
   * Streams every envelope attached to a booking. Used by the envelope-sign
   * page so the primary participant can see everyone already added
   * (themselves included) and the list updates live when they add more.
   */
  streamEnvelopesByBookingId(bookingId: string): Observable<Envelope[]> {
    return this.afs
      .collection<Envelope>(Collection.Envelopes, ref => ref.where('bookingId', '==', bookingId))
      .valueChanges({ idField: 'id' })
      .pipe(
        map(docs => {
          const hydrated = docs.map(d => this.hydrateFromFirestore(d));
          for (const env of hydrated) this.upsertCache(env);
          return hydrated.map(e => this.clone(e));
        }),
      );
  }

  /**
   * Streams a single envelope from Firestore. Emits `undefined` when the
   * envelope doesn't exist (e.g. deleted, or seed-only in-memory IDs).
   * Each emission refreshes the local cache so sync getters and the
   * `envelopes$` snapshot list stay consistent with live state.
   */
  streamEnvelopeById(id: string): Observable<Envelope | undefined> {
    let stream = this.envelopeStreams.get(id);
    if (!stream) {
      stream = this.firestoreService.streamDocument<Envelope>(Collection.Envelopes, id).pipe(
        map(raw => {
          if (!raw) return undefined;
          const env = this.hydrateFromFirestore(raw);
          this.upsertCache(env);
          return this.clone(env);
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
      this.envelopeStreams.set(id, stream);
    }
    return stream;
  }

  /**
   * Looks up a single envelope by id. Cache-first, then falls back to
   * Firestore. Returns `undefined` when the envelope doesn't exist anywhere.
   */
  async getEnvelopeById(id: string): Promise<Envelope | undefined> {
    const cached = this.envelopes.find(e => e.id === id);
    if (cached) return this.clone(cached);

    try {
      const raw = await this.firestoreService.getDocument(Collection.Envelopes, id);
      const env = this.hydrateFromFirestore(raw);
      this.envelopes.push(env);
      this.envelopes$.next(this.getEnvelopes());
      return this.clone(env);
    } catch {
      // getDocument throws when the doc doesn't exist — treat as not-found.
      return undefined;
    }
  }

  /**
   * Sync lookup for an item within an already-cached envelope. Callers
   * should {@link getEnvelopeById} first to guarantee the parent is loaded.
   */
  getItem(envelopeId: string, itemId: string): EnvelopeItem | undefined {
    const env = this.envelopes.find(e => e.id === envelopeId);
    if (!env) return undefined;
    const item = env.items.find(i => i.itemId === itemId);
    return item ? { ...item } : undefined;
  }

  /**
   * Records a signature against a specific envelope item.
   *
   * Routes through the `markEnvelopeItemSigned` Cloud Function so the
   * server enforces the bearer-token contract: knowing an envelope+item id
   * lets you flip pending → signed, but nothing else on the document is
   * mutable through this path. The function rebuilds `signedBy` from
   * validated fields only, so a tampered client payload can't slip in
   * extra keys.
   *
   * Idempotent — re-signing an already-signed item is a server-side no-op.
   * Returns the updated envelope, or `undefined` if the envelope or item
   * cannot be found locally before the call.
   */
  async markItemSigned(payload: EnvelopeItemSignaturePayload): Promise<Envelope | undefined> {
    // Ensure the envelope is in cache (cache hit, or one-shot Firestore load).
    if (!await this.getEnvelopeById(payload.envelopeId)) return undefined;

    const cached = this.envelopes.find(e => e.id === payload.envelopeId);
    const item = cached?.items.find(i => i.itemId === payload.itemId);
    if (!cached || !item) return undefined;
    if (item.status === 'signed') return this.clone(cached);

    // Server is authoritative. The streaming subscription will eventually
    // re-emit with the canonical state (server-issued signedAt/updatedAt);
    // we mutate the cache optimistically here with local times so sync
    // getters and the UI reflect the change immediately.
    const callable = this.afFun.httpsCallable('markEnvelopeItemSigned');
    try {
      await firstValueFrom(callable({
        envelopeId: payload.envelopeId,
        itemId: payload.itemId,
        signedBy: payload.signedBy,
        signedFormData: payload.signedFormData,
      }));
    } catch (error) {
      console.error('Error marking envelope item signed:', error);
      throw error;
    }

    const now = new Date();
    cached.items = cached.items.map(i =>
      i.itemId === payload.itemId
        ? {
          ...i,
          status: 'signed' as const,
          signedAt: now,
          signedBy: { ...payload.signedBy },
          ...(payload.signedFormData ? { signedFormData: payload.signedFormData } : {}),
        }
        : { ...i },
    );
    cached.status = this.deriveStatus(cached.items);
    cached.updatedAt = now;

    this.envelopes$.next(this.getEnvelopes());
    return this.clone(cached);
  }

  /**
   * Creates a new envelope for a single participant.
   *
   * Routes through the `createEnvelope` Cloud Function so the server can:
   *   - Reject envelopes referencing nonexistent rentals (no orphan docs)
   *   - Atomically arrayUnion the new id onto `rental.envelopeIds`
   *     (callers no longer need a separate `rentalService.addEnvelopeIdToRental` call)
   *   - Server-rebuild the envelope shape from validated fields only,
   *     dropping unknown keys clients might inject
   *   - Force `signedAt` to server time on any pre-signed items so the
   *     kiosk-QR flow can't be backdated
   *
   * The id, `createdAt`, `updatedAt`, and `status` are assigned server-side
   * — the response includes them so the client cache stays consistent
   * without an extra read.
   */
  async createEnvelope(input: Omit<Envelope, 'id' | 'createdAt' | 'updatedAt' | 'status'>): Promise<Envelope> {
    const callable = this.afFun.httpsCallable('createEnvelope');
    let saved: Envelope;
    try {
      saved = await firstValueFrom(callable(input)) as Envelope;
    } catch (error) {
      console.error('Error creating envelope:', error);
      throw error;
    }

    this.envelopes.push(this.clone(saved));
    this.envelopes$.next(this.getEnvelopes());
    return this.clone(saved);
  }

  /**
   * Appends new pending items to an existing envelope. Called when the
   * partner-side Add Product flow brings in waivers that existing
   * participants haven't been asked about yet. Duplicates by waiverId
   * are silently skipped by the CF, so callers can safely pass the full
   * required-waiver set — only the actually-new rows land. Signed items
   * on the envelope are preserved unchanged.
   */
  async appendItemsToEnvelope(envelopeId: string, items: EnvelopeItem[]): Promise<{ appended: number; envelope: Envelope }> {
    const callable = this.afFun.httpsCallable('appendItemsToEnvelope');
    let result: { appended: number; envelope: Envelope };
    try {
      result = await firstValueFrom(callable({ envelopeId, items })) as { appended: number; envelope: Envelope };
    } catch (error) {
      console.error('Error appending items to envelope:', error);
      throw error;
    }
    // Keep the in-memory cache in sync so components listening on
    // envelopes$ see the appended items without a stream round-trip.
    const idx = this.envelopes.findIndex(e => e.id === envelopeId);
    if (idx !== -1) {
      this.envelopes[idx] = this.clone(result.envelope);
      this.envelopes$.next(this.getEnvelopes());
    }
    return { appended: result.appended, envelope: this.clone(result.envelope) };
  }

  /**
   * Builds the public `/envelope/:id` URL used in outbound emails and SMS.
   * Does not touch the envelope itself — purely a URL-formatting helper.
   */
  generateEnvelopeLink(envelopeId: string): string {
    return `${window.location.origin}/envelope/${envelopeId}`;
  }

  /**
   * Queues an envelope-link email via the `sendEnvelopeEmail` Cloud Function.
   *
   * The server validates the envelope exists, builds the link from the
   * configured app domain (clients can't inject arbitrary URLs), and writes
   * to the `emails` queue. Use this anywhere an envelope-bound email needs
   * to go out (initial post-checkout send, partner-side resend, envelope-
   * side Add Participant). For non-envelope sends (e.g. the partner's
   * waiver-list send-link modal where no envelope exists yet), keep using
   * {@link WaiverBuilderService.sendWaiverEmail}.
   */
  async sendEnvelopeEmail(envelopeId: string, recipientEmail: string, recipientName?: string): Promise<void> {
    const callable = this.afFun.httpsCallable('sendEnvelopeEmail');
    try {
      await firstValueFrom(callable({
        envelopeId,
        recipientEmail,
        recipientName: recipientName || '',
      }));
    } catch (error) {
      console.error('Error sending envelope email:', error);
      throw error;
    }
  }

  /**
   * Opens a Swal showing the manual signing link when an envelope was
   * created but the email dispatch (or resend) failed. Without this, the
   * caller has to log to the console and the partner has no way to get
   * the link to the signer.
   *
   * @param envelopeId The envelope to build a link for.
   * @param opts.recipientLabel Name shown in the message, e.g. "Sarah".
   * @param opts.recipientEmail Email that failed (if any) — included in
   *        the message so the partner can confirm which signer to contact.
   * @param opts.audience 'partner' (default) frames the copy as "share
   *        with the signer"; 'customer' frames it as "use this link
   *        yourself" for post-checkout failures.
   */
  async showEmailFailureFallback(
    envelopeId: string,
    opts: { recipientLabel?: string; recipientEmail?: string; audience?: 'partner' | 'customer' } = {},
  ): Promise<void> {
    const link = this.generateEnvelopeLink(envelopeId);
    const audience = opts.audience || 'partner';
    const escape = (v: string) => v.replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
    const labelHtml = opts.recipientLabel ? `<strong>${escape(opts.recipientLabel)}</strong>` : null;
    const emailHtml = opts.recipientEmail ? `<code>${escape(opts.recipientEmail)}</code>` : null;

    const partnerLead = labelHtml
      ? `The waiver email to ${labelHtml}${emailHtml ? ` (${emailHtml})` : ''} couldn't be delivered. Share this link with them directly:`
      : `The waiver email couldn't be delivered. Share this link with the signer directly:`;
    const customerLead = `We couldn't send the waiver email to ${emailHtml || 'your address'}. You can sign right now using this direct link:`;

    const lead = audience === 'customer' ? customerLead : partnerLead;
    const linkHtml = escape(link);

    const result = await Swal.fire({
      title: audience === 'customer' ? "We Couldn't Email You" : 'Email Failed to Send',
      icon: 'warning',
      html: `
        <div style="text-align: left; font-size: 0.9rem;">
          <p>${lead}</p>
          <input id="envelope-fallback-link" type="text" readonly
                 value="${linkHtml}"
                 style="width: 100%; padding: 0.5rem; font-family: monospace;
                        font-size: 0.85rem; border: 1px solid #ccc;
                        border-radius: 4px; background: #f8f9fa;" />
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Copy Link',
      cancelButtonText: 'Close',
      didOpen: () => {
        const el = document.getElementById('envelope-fallback-link') as HTMLInputElement | null;
        el?.focus();
        el?.select();
      },
    });

    if (result.isConfirmed) {
      try {
        await navigator.clipboard.writeText(link);
        Swal.fire({
          icon: 'success',
          title: 'Link copied',
          text: 'The signing link is on your clipboard.',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch {
        // Clipboard API can fail in non-secure contexts or older browsers.
        // Re-open the dialog so the partner can copy manually.
        await this.showEmailFailureFallback(envelopeId, opts);
      }
    }
  }

  /**
   * For every envelope on a booking, drop any pending items whose waiver id
   * is in `removeWaiverIds`. Signed items are preserved unconditionally; any
   * envelope that ends up empty is deleted by the CF and its id removed from
   * `rental.envelopeIds`.
   *
   * Used after a partner removes a product from a rental — waivers that
   * were only required by the removed product become orphaned and should
   * not continue to be requested from signers.
   *
   * Best-effort per envelope: a failure on one envelope is logged and the
   * loop continues so a single bad envelope doesn't block cleanup of the
   * others. The caller should not roll back the product removal if this
   * throws — the envelope state is recoverable but the rental state is what
   * the partner expected to change.
   */
  async pruneWaiversFromBooking(bookingId: string, removeWaiverIds: string[]): Promise<void> {
    if (!bookingId || !Array.isArray(removeWaiverIds) || removeWaiverIds.length === 0) return;

    // One-shot server read instead of subscribing — we want the current
    // state, not a live stream, and we'll be done in milliseconds.
    const snap = await this.afs
      .collection<Envelope>(Collection.Envelopes)
      .ref
      .where('bookingId', '==', bookingId)
      .get();

    const callable = this.afFun.httpsCallable('pruneEnvelopeItems');
    const removeSet = new Set(removeWaiverIds);

    for (const doc of snap.docs) {
      const envelope = doc.data() as Envelope;
      const items = Array.isArray(envelope.items) ? envelope.items : [];
      // Skip envelopes with nothing to prune so we don't make pointless CF calls.
      const hasMatch = items.some(i => i && i.status === 'pending' && removeSet.has(i.waiverId));
      if (!hasMatch) continue;

      try {
        const result = await firstValueFrom(callable({
          envelopeId: doc.id,
          removeWaiverIds,
        })) as { deleted: boolean; envelopeId?: string; envelope?: Envelope };

        // Keep the local cache + stream consistent with what the CF just did.
        if (result?.deleted) {
          this.envelopes = this.envelopes.filter(e => e.id !== doc.id);
        } else if (result?.envelope) {
          this.upsertCache(this.hydrateFromFirestore(result.envelope));
        }
      } catch (err) {
        console.error(`Failed to prune envelope ${doc.id}:`, err);
      }
    }
    this.envelopes$.next(this.getEnvelopes());
  }

  /**
   * Hard-revokes a participant by deleting their envelope outright.
   *
   * Routes through the `revokeEnvelope` Cloud Function so the delete
   * + `rental.envelopeIds` arrayRemove land in a single transaction.
   * The CF is idempotent — calling on an already-revoked or never-
   * existed envelope returns `{ revoked: false }` without throwing.
   *
   * After a successful call the local cache is purged so the partner-
   * side checklist / list views drop the row immediately, instead of
   * waiting for the Firestore stream to catch up (which it eventually
   * does anyway).
   *
   * @returns true when the CF actually performed a deletion, false
   *   when the envelope was already gone (still a non-error path).
   */
  async revokeEnvelope(envelopeId: string): Promise<boolean> {
    if (!envelopeId) return false;
    let result: { revoked?: boolean } = {};
    try {
      // Prod / deployed: route through the /api/revokeEnvelope Hosting
      // rewrite — avoids the allUsers-invoker IAM grant that the org
      // policy currently blocks for new functions.
      //
      // Local `ng serve` (localhost:4200): there's no Hosting layer, so
      // /api/* returns 404. Fall back to httpsCallable — for that to
      // succeed against the deployed dev CF, the function needs allUsers
      // invoker OR the Functions emulator has to be running (which
      // AngularFireFunctions is wired to when environment.emulators.functions
      // is true).
      const useDirectCallable = !environment.production && this.isLocalHostServe();
      if (useDirectCallable) {
        const callable = this.afFun.httpsCallable('revokeEnvelope');
        result = (await firstValueFrom(callable({ envelopeId }))) as { revoked?: boolean };
      } else {
        result = await this.callViaHostingRewrite<{ revoked?: boolean }>(
          'revokeEnvelope',
          { envelopeId },
        );
      }
    } catch (error) {
      console.error('Error revoking envelope:', error);
      throw error;
    }

    // Purge from local cache regardless of `revoked` flag — if the doc
    // is gone server-side (revoked=true) or was already gone (false),
    // either way it should not be in our cache anymore.
    const before = this.envelopes.length;
    this.envelopes = this.envelopes.filter(e => e.id !== envelopeId);
    if (this.envelopes.length !== before) {
      this.envelopes$.next(this.getEnvelopes());
    }
    return !!result?.revoked;
  }

  /**
   * True when we're running under `ng serve` against localhost:4200 —
   * the setup where the /api/* Hosting rewrites don't resolve. Matches
   * the "ng serve dev" heuristic used by FirebaseEmulatorsService so
   * the two agree on when to switch to non-Hosting call paths.
   */
  private isLocalHostServe(): boolean {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    const port = window.location.port;
    return (host === 'localhost' || host === '127.0.0.1') && port === '4200';
  }

  /**
   * Generates a unique id for an envelope item. Intended for callers who
   * need to construct `items` before handing them to {@link createEnvelope}.
   */
  generateItemId(): string {
    return `item_${Math.random().toString(36).substring(2, 10)}_${Date.now()}`;
  }

  /**
   * Derives an envelope's overall status from its items:
   * - empty or all-pending → `pending`
   * - all signed → `complete`
   * - mixed → `partial`
   */
  private deriveStatus(items: EnvelopeItem[]): EnvelopeStatus {
    if (items.length === 0) return 'pending';
    const signedCount = items.filter(i => i.status === 'signed').length;
    if (signedCount === 0) return 'pending';
    if (signedCount === items.length) return 'complete';
    return 'partial';
  }

  /**
   * Replaces the cached copy of an envelope with a fresh hydrated version,
   * or appends it if not yet cached. Used by the streaming getter so
   * subsequent sync reads (`getItem`, `getEnvelopes`) stay in sync with
   * Firestore without a round-trip.
   */
  private upsertCache(env: Envelope): void {
    const idx = this.envelopes.findIndex(e => e.id === env.id);
    if (idx === -1) {
      this.envelopes.push(env);
    } else {
      this.envelopes[idx] = env;
    }
    this.envelopes$.next(this.getEnvelopes());
  }

  /**
   * Deep-copies an envelope (participant + every item, including nested
   * `signedBy`) so callers cannot mutate the in-memory store by holding a
   * reference to a returned envelope. Every public getter and stream
   * emission routes through here.
   */
  private clone(env: Envelope): Envelope {
    return {
      ...env,
      participant: { ...env.participant },
      items: env.items.map(i => ({
        ...i,
        signedBy: i.signedBy ? { ...i.signedBy } : undefined,
        // signedFormData carries customer-entered values + signature data
        // URLs. Shallow-clone the dictionary so cache callers mutating a
        // field value don't pollute the in-memory store. Values themselves
        // are primitives (strings/booleans) so a one-level clone is enough.
        signedFormData: i.signedFormData ? { ...i.signedFormData } : undefined,
      })),
    };
  }

  /**
   * Normalizes a Firestore doc into an `Envelope`: Timestamps → Dates on the
   * top-level audit fields and on every item's `signedAt`. The id field is
   * assumed to have been set by FirestoreService.getDocument.
   */
  private hydrateFromFirestore(raw: Envelope): Envelope {
    const toDate = (val: unknown): Date | undefined => {
      if (!val) return undefined;
      if (val instanceof Date) return val;
      const withToDate = val as { toDate?: () => Date };
      if (typeof withToDate.toDate === 'function') return withToDate.toDate();
      if (typeof val === 'string' || typeof val === 'number') return new Date(val);
      return undefined;
    };
    return {
      ...raw,
      createdAt: toDate(raw.createdAt) || new Date(),
      updatedAt: toDate(raw.updatedAt) || new Date(),
      expiresAt: toDate(raw.expiresAt),
      items: (raw.items || []).map(i => ({
        ...i,
        signedAt: toDate(i.signedAt),
      })),
    };
  }

}
